import type { GridColumnVisibilityModel } from "@mui/x-data-grid";

/** The visibility model is sparse: only an explicit `false` hides a column; absent and `true` both show it. */
const isShown = (model: GridColumnVisibilityModel, field: string): boolean => model[field] !== false;

/** Where a field absent from `sequence` belongs: after its nearest natural predecessor, else before its nearest successor. */
function insertionIndexFor(naturalIndex: number, reorderableNatural: readonly string[], sequence: readonly string[]): number {
	for (let j = naturalIndex - 1; j >= 0; j--) {
		const at = sequence.indexOf(reorderableNatural[j]);
		if (at !== -1) return at + 1;
	}
	for (let j = naturalIndex + 1; j < reorderableNatural.length; j++) {
		const at = sequence.indexOf(reorderableNatural[j]);
		if (at !== -1) return at;
	}
	return 0;
}

/**
 * The display sequence of the reorderable fields, given a persisted order. Saved fields that no longer
 * exist (or are pinned) are dropped and duplicates collapse to their first occurrence. A reorderable field
 * the saved order doesn't mention — typically a column added after the order was saved — is slotted right
 * after its nearest natural-order predecessor in the sequence (falling back to just before its nearest
 * successor, then the start). Appending it at the end instead would tear it out of its header band.
 */
export function resolveReorderableSequence(savedOrder: readonly string[], reorderableNatural: readonly string[]): string[] {
	const known = new Set(reorderableNatural);
	const seen = new Set<string>();
	const sequence: string[] = [];
	for (const field of savedOrder) {
		if (known.has(field) && !seen.has(field)) {
			sequence.push(field);
			seen.add(field);
		}
	}
	// Natural order, so a run of several missing neighbours lands together and keeps its own order.
	reorderableNatural.forEach((field, naturalIndex) => {
		if (seen.has(field)) return;
		sequence.splice(insertionIndexFor(naturalIndex, reorderableNatural, sequence), 0, field);
		seen.add(field);
	});
	return sequence;
}

/**
 * The full display order SystemDataGrid renders for a persisted order: pinned (non-reorderable) fields hold
 * their natural slots no matter what the saved order says — a legacy or corrupt order that interleaves
 * them must never move one — and the reorderable fields fill the remaining slots in
 * `resolveReorderableSequence` order. Anything mirroring the grid's layout outside it (e.g. a saved view)
 * must use this so it agrees with what is on screen.
 */
export function resolveColumnDisplayOrder(savedOrder: readonly string[], naturalOrder: readonly string[], pinnedFields: ReadonlySet<string>): string[] {
	const sequence = resolveReorderableSequence(
		savedOrder,
		naturalOrder.filter((field) => !pinnedFields.has(field)),
	);
	let cursor = 0;
	return naturalOrder.map((field) => (pinnedFields.has(field) ? field : sequence[cursor++]));
}

export interface BandPlacementArgs {
	/** Every column field in the current display order, hidden and pinned ones included. */
	displayOrder: readonly string[];
	/** Every column field in the `columns` prop order — ties several newly-shown columns to their natural order. */
	naturalOrder: readonly string[];
	/** `field → headerGroup` for the columns that belong to a header band. */
	headerGroupByField: ReadonlyMap<string, string>;
	/** Non-reorderable columns; they are never moved. */
	pinnedFields: ReadonlySet<string>;
	previousModel: GridColumnVisibilityModel;
	nextModel: GridColumnVisibilityModel;
}

interface GroupPlacementArgs {
	order: string[];
	naturalOrder: readonly string[];
	group: string;
	newlyVisible: ReadonlySet<string>;
	headerGroupByField: ReadonlyMap<string, string>;
	previousModel: GridColumnVisibilityModel;
	nextModel: GridColumnVisibilityModel;
}

/** The band's contiguous runs within the visible sequence, each as its fields in display order. */
function groupRuns(visible: readonly string[], group: string, headerGroupByField: ReadonlyMap<string, string>): string[][] {
	const runs: string[][] = [];
	let current: string[] | null = null;
	for (const field of visible) {
		if (headerGroupByField.get(field) !== group) {
			current = null;
			continue;
		}
		if (!current) {
			current = [];
			runs.push(current);
		}
		current.push(field);
	}
	return runs;
}

function placeGroupInBand({ order, naturalOrder, group, newlyVisible, headerGroupByField, previousModel, nextModel }: GroupPlacementArgs): string[] {
	// "Settled" = a band member that was already on screen and stays on screen.
	const isSettled = (field: string) => !newlyVisible.has(field) && isShown(previousModel, field) && isShown(nextModel, field);
	const visible = order.filter((f) => isShown(nextModel, f));
	const runs = groupRuns(visible, group, headerGroupByField);
	const anchoredRuns = runs.filter((run) => run.some(isSettled));
	if (anchoredRuns.length === 0) return order;

	// Only orphans move: a newly-shown column sharing a run with ANY settled member of its band already
	// reads as part of that band — even when the band is split elsewhere by an earlier drag — so it stays
	// put, and re-showing a column in its natural slot never becomes a "customized" order. Filtering the
	// natural order keeps several orphans of one band in their natural relative order.
	const orphaned = new Set(runs.filter((run) => !run.some(isSettled)).flat());
	const strays = naturalOrder.filter((f) => newlyVisible.has(f) && orphaned.has(f));
	if (strays.length === 0) return order;

	// Orphans join the band's LARGEST on-screen run — its main block, which a user dragging one column
	// away leaves behind. A tie goes to the leftmost run (strict `>` keeps the first), the stable choice
	// that also matches reading order.
	const target = anchoredRuns.reduce((best, run) => (run.length > best.length ? run : best));
	const targetEnd = target[target.length - 1];
	const straySet = new Set(strays);
	const remaining = order.filter((f) => !straySet.has(f));
	const insertAt = remaining.indexOf(targetEnd) + 1;
	return [...remaining.slice(0, insertAt), ...strays, ...remaining.slice(insertAt)];
}

/**
 * Keeps each header band contiguous when the column picker reveals columns. A column that goes hidden →
 * visible in this change and lands next to an already-visible member of its band stays where it is; one
 * that would open a separate band cell (an orphan) moves to immediately after the band's largest
 * on-screen run (leftmost on a tie). Several orphans of one band keep their natural relative order. A
 * band with nothing already visible leaves its newly-shown columns where they are.
 * Pinned and ungrouped columns never move. Returns the new full display order, or `null` when nothing
 * moves — the caller persists a non-null result exactly like a drag.
 */
export function placeNewlyVisibleInBands({
	displayOrder,
	naturalOrder,
	headerGroupByField,
	pinnedFields,
	previousModel,
	nextModel,
}: BandPlacementArgs): string[] | null {
	const newlyVisible = new Set(
		displayOrder.filter((f) => headerGroupByField.has(f) && !pinnedFields.has(f) && !isShown(previousModel, f) && isShown(nextModel, f)),
	);
	if (newlyVisible.size === 0) return null;

	const groups = new Set<string>();
	for (const field of newlyVisible) {
		const group = headerGroupByField.get(field);
		if (group !== undefined) groups.add(group);
	}
	let order = [...displayOrder];
	for (const group of groups) {
		order = placeGroupInBand({ order, naturalOrder, group, newlyVisible, headerGroupByField, previousModel, nextModel });
	}
	const changed = order.some((field, index) => field !== displayOrder[index]);
	return changed ? order : null;
}

/**
 * The full display order after a drag in a list that shows only SOME of the columns (the column picker leaves
 * out identity and action columns): the dragged fields, in their new order, take the slots those same fields
 * held, and every other column stays exactly where it was.
 */
export function slotReorderedFields(fieldOrder: readonly string[], reordered: readonly string[]): string[] {
	const moved = new Set(reordered);
	let next = 0;
	return fieldOrder.map((field) => (moved.has(field) ? reordered[next++] : field));
}
